import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import '../../../css/Pages.css';
// import custom hooks
import UseMessageSelection from '../UseMessageSelection';
import useMessages from '../UseMessages';
// import custome logic
import FileDownloadService from '../FileDownloadService';
import { hasDownloadPermission, hasRetryPermission, isMessageTooOld } from '../DashboardUtilities';
// import sub components
import FilterBar from './Filterbar';
import ActionBar from './ActionBar';
import MessageRow from './MessageRow';
import PaginationControls from './PaginationControls';
import Notification from './Notification';
import RenderOnRole from "../../../Utilities/RenderOnRole";

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const BerichtDownloadComponent = ({ interface: interfaceConfig, axiosInstance, onRefresh }) => {
  const { t } = useTranslation();
  const [pageNumber, setPageNumber] = useState(0);
  const [pageCount, setPageCount] = useState(50);

  const [filters, setFilters] = useState({
    interfaceId: interfaceConfig?.id || '',
    startDate: '',
    endDate: '',
    ontvangerGLN: '',
    verzenderGLN: '',
    functioneelId: '',
    status: '',
    pageNumber: pageNumber,
    pageCount: pageCount,
  });

  const [sorting, setSorting] = useState({
    field: 'VerzondenDatumtijd',
    direction: 'Descending',
  });

  // Update filters when pagination changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      pageNumber,
      pageCount,
    }));
  }, [pageNumber, pageCount]);

  // Update interfaceId when interface changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      interfaceId: interfaceConfig?.id || '',
    }));
  }, [interfaceConfig?.id]);

  const { messages, loading, error, totalCount, relatiesVerzend, relatiesOntvangt } = useMessages(filters, sorting, axiosInstance);
  const { selectedIds, toggleSelection, selectAll, clearSelection, isSelected } = UseMessageSelection();
  
  const [notifications, setNotifications] = useState([]);

  const selectedMessages = useMemo(() => 
    messages.filter(m => selectedIds.has(m.id)),
    [messages, selectedIds]
  );

  const hasOldMessages = useMemo(() => 
    selectedMessages.some(m => isMessageTooOld(m.verzonden_datumtijd)),
    [selectedMessages]
  );

  const handleDownload = useCallback(async () => {
    if (!axiosInstance) return;
    
    const results = [];
    
    for (const message of selectedMessages) {
      try {
        const url = `${process.env.REACT_APP_API_URL}/Berichten/Download?berichtId=${message.id}`;
        const response = await axiosInstance.get(url);
        
        if (response.status === 200) {
          for (const item of response.data) {
            const data = item.messageText;
            const extension = item.fileExtension;
            const tag = item.tag;
            
            let fileName = tag ? `${tag}_` : '';
            fileName += message.functioneelID || `${message.verzend_rela_naam}_naar_${message.ontvangt_rela_naam}_${message.verzonden_datumtijd}`;
            
            FileDownloadService.downloadFile(data, fileName, extension);
          }
          
          results.push({
            functioneelId: message.functioneelID,
            success: true,
            status: t('messageDownload.downloadSuccess')
          });
        }
      } catch (err) {
        results.push({
          functioneelId: message.functioneelID,
          success: false,
          status: err.response?.data || err.message
        });
      }
    }
    
    setNotifications(results);
    clearSelection();
  }, [selectedMessages, clearSelection, axiosInstance, interfaceConfig]);

  const handleRetry = useCallback(async (fileContent = null) => {
    if (!axiosInstance) return [];
    
    const results = [];
    let messagesToRetry = selectedMessages;
    
    // If fileContent provided but no messages selected, create a dummy message
    if (fileContent && messagesToRetry.length === 0) {
      messagesToRetry = [{ connectmessageId: 0, id: 0, functioneelID: t('messageDownload.newMessage') }];
    }
    
    for (const message of messagesToRetry) {
      try {
        const url = `${process.env.REACT_APP_API_URL}/Berichten/Retry?InterfaceId=${interfaceConfig.id}&ConnectmessageId=${message.connectmessageId}&berichtId=${message.id}`;
        const body = JSON.stringify({ message: fileContent || '' });
        
        await axiosInstance.post(url, body, {
          headers: { 'Content-Type': 'application/json' }
        });
        
        results.push({
          functioneelId: message.functioneelID,
          success: true,
          status: t('messageDownload.retrySuccess')
        });
      } catch (err) {
        results.push({
          functioneelId: message.functioneelID,
          success: false,
          status: typeof err.response?.data === 'string' 
            ? err.response.data 
            : (err.response?.data?.message || err.message || t('messageDownload.retryErrorNoResponse'))
        });
      }
    }
    
    setNotifications(results);
    clearSelection();
        return results;
  }, [selectedMessages, clearSelection, axiosInstance, interfaceConfig]);

  const handleRetryWithCorrection = useCallback(async (file) => {
    if (!file) {
      setNotifications([{
        functioneelId: 'N/A',
        success: false,
        status: t('messageDownload.noFileSelected')
      }]);
      return;
    }

    try {
      const content = await file.text();
      return await handleRetry(content);
    } catch (err) {
      setNotifications([{
        functioneelId: file.name,
        success: false,
        status: err.message
      }]);
      return [{
        functioneelId: file.name,
        success: false,
        status: err.message
      }];
    }
  }, [handleRetry]);

  const handleSortChange = (field) => {
    let actualField = field;
    
    // Check if interface sender is Stichting SCSN
    const isSenderSCSN = interfaceConfig?.verzenderNaam?.includes('Stichting SCSN');
    // Check if interface receiver is Stichting SCSN
    const isReceiverSCSN = interfaceConfig?.ontvangerNaam?.includes('Stichting SCSN');
    
    // Adjust field name only for the party that is SCSN
    if (field === 'ZenderNaam' && isSenderSCSN) {
      actualField = 'ZenderNaamSCSN';
    } else if (field === 'OntvangerNaam' && isReceiverSCSN) {
      actualField = 'OntvangerNaamSCSN';
    }
    
    setSorting(prev => ({
      field: actualField,
      direction: prev.field === actualField && prev.direction === 'Ascending' ? 'Descending' : 'Ascending',
    }));
  };

  const handlePageChange = (newPage) => {
    setPageNumber(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageCount(newPageSize);
    setPageNumber(0); // Reset to first page (0-based) when changing page size
  };

  const handleResetFilters = useCallback(() => {
    setFilters({
      interfaceId: interfaceConfig?.id || '',
      startDate: '',
      endDate: '',
      ontvangerGLN: '',
      verzenderGLN: '',
      functioneelId: '',
      status: '',
      pageNumber: 0,
      pageCount: pageCount,
    });
    setPageNumber(0);
  }, [interfaceConfig?.id, pageCount]);

  const handleRefreshMessages = useCallback(() => {
    // Force a refetch by updating filters object reference
    setFilters(prev => ({ ...prev }));
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedIds.size === messages.length && messages.length > 0) {
      clearSelection();
    } else {
      selectAll(messages);
    }
  }, [messages, selectedIds.size, selectAll, clearSelection]);

  const allSelected = useMemo(() => 
    messages.length > 0 && selectedIds.size === messages.length,
    [messages.length, selectedIds.size]
  );

  // Determine if SCSN columns should be shown (if any values exist)
  const hasScsnValues = useMemo(() => {
    return messages.some(msg => {
      const glnZender = msg.glN_zender?.toString().trim();
      const glnOntvangt = msg.glN_ontvanger?.toString().trim();
      return (glnZender && glnZender.toLowerCase() !== 'null') || 
             (glnOntvangt && glnOntvangt.toLowerCase() !== 'null');
    });
  }, [messages]);

  // Check user permissions
  const canDownload = useMemo(() => hasDownloadPermission(), []);
  const canRetry = useMemo(() => hasRetryPermission(), []);

  return (
    <div className="p-6 max-w-full">
      <h2 className="text-3xl font-bold mb-6 text-gray-800">{t('dashboard.messages')}</h2>

      <FilterBar
        filters={filters}
        onFilterChange={setFilters}
        onResetFilters={handleResetFilters}
        relatiesVerzend={relatiesVerzend}
        relatiesOntvangt={relatiesOntvangt}
      />

      <ActionBar
        onDownload={handleDownload}
        onRetry={handleRetry}
        onRetryWithCorrection={handleRetryWithCorrection}
        onRefresh={handleRefreshMessages}
        selectedCount={selectedIds.size}
        hasListener={interfaceConfig?.listener || true}
        hasOldMessages={hasOldMessages}
        canDownload={canDownload}
        canRetry={canRetry}
      />

      {error && (
        <div className="error-alert">
          <h3>{t('messageDownload.errorLoadingMessages')}</h3>
          <p>{error}</p>
        </div>
      )}

      <PaginationControls
        currentPage={pageNumber}
        pageSize={pageCount}
        totalRecords={totalCount}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      <div className="table-wrapper">
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th className="w-12">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={handleSelectAll}
                    className="w-4 h-4 cursor-pointer"
                    title={t('messageDownload.selectAll')}
                  />
                </th>
                <th
                  className="cursor-pointer"
                  onClick={() => handleSortChange('ZenderNaam')}
                >
                  <div className="flex items-center gap-1">
                    {t('interface.sender')}
                    {(sorting.field === 'ZenderNaam' || sorting.field === 'ZenderNaamSCSN') && (
                      <span className="sort-indicator">{sorting.direction === 'Ascending' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th
                  className="cursor-pointer"
                  onClick={() => handleSortChange('ZenderGLN')}
                  style={{ display: hasScsnValues ? 'table-cell' : 'none' }}
                >
                  <div className="flex items-center gap-1">
                    {t('messageDownload.scsnNumber')}
                    {sorting.field === 'ZenderGLN' && (
                      <span className="sort-indicator">{sorting.direction === 'Ascending' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th
                  className="cursor-pointer"
                  onClick={() => handleSortChange('OntvangerNaam')}
                >
                  <div className="flex items-center gap-1">
                    {t('interface.receiver')}
                    {(sorting.field === 'OntvangerNaam' || sorting.field === 'OntvangerNaamSCSN') && (
                      <span className="sort-indicator">{sorting.direction === 'Ascending' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th
                  className="cursor-pointer"
                  onClick={() => handleSortChange('OntvangerGLN')}
                  style={{ display: hasScsnValues ? 'table-cell' : 'none' }}
                >
                  <div className="flex items-center gap-1">
                    {t('messageDownload.scsnNumber')}
                    {sorting.field === 'OntvangerGLN' && (
                      <span className="sort-indicator">{sorting.direction === 'Ascending' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <RenderOnRole roles= {["Administrator", "SD_Medewerker"]}>
                  <th
                    className="cursor-pointer"
                    onClick={() => handleSortChange('id')}
                  >
                    <div className="flex items-center gap-1">
                      {t('messageDownload.berichtIdLabel')}
                      {sorting.field === 'id' && (
                        <span className="sort-indicator">{sorting.direction === 'Ascending' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                </RenderOnRole>
                <th
                  className="cursor-pointer"
                  onClick={() => handleSortChange('functioneelID')}
                >
                  <div className="flex items-center gap-1">
                    {t('messageDownload.functioneelIdLabel')}
                    {sorting.field === 'functioneelID' && (
                      <span className="sort-indicator">{sorting.direction === 'Ascending' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th
                  className="cursor-pointer"
                  onClick={() => handleSortChange('status')}
                >
                  <div className="flex items-center gap-1">
                    {t('subscriptions.status')}
                    {sorting.field === 'status' && (
                      <span className="sort-indicator">{sorting.direction === 'Ascending' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th
                  className="cursor-pointer"
                  onClick={() => handleSortChange('VerzondenDatumtijd')}
                >
                  <div className="flex items-center gap-1">
                    {t('messageDownload.received')}
                    {sorting.field === 'VerzondenDatumtijd' && (
                      <span className="sort-indicator">{sorting.direction === 'Ascending' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th>{t('messageDownload.lastUpdate')}</th>
                <th
                  className="cursor-pointer"
                >
                  <div className="flex items-center gap-1">
                    {t('messageDownload.agent')}
                    {sorting.field === 'agentname' && (
                      <span className="sort-indicator">{sorting.direction === 'Ascending' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="text-center text-gray-500 py-12">
                    {t('messageDownload.loadingMessages')}
                  </td>
                </tr>
              ) : messages.length > 0 ? (
                messages.map((message, index) => (
                  <MessageRow
                    key={message.id}
                    message={message}
                    index={index}
                    isSelected={isSelected(message.id)}
                    onToggle={(id, idx, isShift) => toggleSelection(id, idx, isShift, messages)}
                    hasScsnValues={hasScsnValues}
                  />
                ))
              ) : (
                <tr>
                  <td colSpan={10} className="text-center text-gray-500 py-12">
                    {t('messageDownload.noMessages')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Notification
        messages={notifications}
        onClose={() => setNotifications([])}
      />
    </div>
  );
};

export default BerichtDownloadComponent;